/* ═══════════════════════════════════════════════════════════════════════════════
   DashboardFilterBar: Premium Filter Modal
   
   Architecture:
   - Trigger: compact pill button showing active filter count + removable pills
   - Modal: full overlay with staggered section animations
   - Layout: 2-column grid (Projects | Builders) + Period + Status
   - UX: Apply/Clear actions, ESC to close, click-outside dismissal
   ═══════════════════════════════════════════════════════════════════════════════ */

import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  SlidersHorizontal,
  X,
  Check,
  Search,
  FolderOpen,
  Users,
  Calendar,
  Activity,
} from 'lucide-react';
import { useDashboardFilter } from '../../contexts/useDashboardFilterHooks';
import type { ProjectWithDetails } from '../../hooks/useProjects';
import type { DatePreset } from '../../types/dashboard-filters';
import './DashboardFilterBar.css';

// ─── Types ───────────────────────────────────────────────────────────────────

interface DashboardFilterBarProps {
  projects: ProjectWithDetails[];
}

interface FilterListProps {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (selected: string[]) => void;
  searchPlaceholder?: string;
}

// ─── Date Preset Config ──────────────────────────────────────────────────────

const DATE_PRESETS: { value: DatePreset; label: string }[] = [
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'ytd', label: 'YTD' },
  { value: 'last_12', label: 'Last 12M' },
  { value: 'all_time', label: 'All Time' },
  { value: 'custom', label: 'Custom' },
];

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

function getYearOptions(): number[] {
  const current = new Date().getFullYear();
  const years: number[] = [];
  for (let y = current; y >= current - 10; y--) {
    years.push(y);
  }
  return years;
}

// ─── Filter List Component (no clipping, full-height in modal) ───────────────

function FilterList({
  options,
  selected,
  onChange,
  searchPlaceholder = 'Search...',
}: FilterListProps) {
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>(null);

  const filteredOptions = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const toggleOption = useCallback(
    (value: string) => {
      if (selectedSet.has(value)) {
        onChange(selected.filter((v) => v !== value));
      } else {
        onChange([...selected, value]);
      }
    },
    [selected, selectedSet, onChange],
  );

  const handleSelectAll = useCallback(() => {
    if (selected.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map((o) => o.value));
    }
  }, [selected, options, onChange]);

  return (
    <div className="dfb-list-wrapper">
      <div className="dfb-list-search">
        <Search size={14} className="dfb-list-search-icon" />
        <input
          ref={searchRef}
          type="text"
          placeholder={searchPlaceholder}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {!search && options.length > 2 && (
        <div className="dfb-select-all">
          <button
            type="button"
            className="dfb-select-all-btn"
            onClick={handleSelectAll}
          >
            {selected.length === options.length ? 'Deselect all' : 'Select all'}
          </button>
          <span className="dfb-select-count-label">
            {selected.length} of {options.length}
          </span>
        </div>
      )}
      <div className="dfb-list-items">
        {filteredOptions.length === 0 ? (
          <div className="dfb-list-empty">No results found</div>
        ) : (
          filteredOptions.map((option) => {
            const isSelected = selectedSet.has(option.value);
            return (
              <div
                key={option.value}
                className={`dfb-list-item ${isSelected ? 'selected' : ''}`}
                onClick={() => toggleOption(option.value)}
              >
                <div className={`dfb-list-checkbox ${isSelected ? 'checked' : ''}`}>
                  {isSelected && <Check size={11} color="white" strokeWidth={3} />}
                </div>
                <span>{option.label}</span>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ─── Filter Modal Content ────────────────────────────────────────────────────

function FilterModal({
  projects,
  onClose,
}: {
  projects: ProjectWithDetails[];
  onClose: () => void;
}) {
  const { filters, dispatch, isFiltered } = useDashboardFilter();
  const [closing, setClosing] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  const yearOptions = useMemo(() => getYearOptions(), []);

  // ── Cross-filter logic (Power BI slicer cascade) ───────────────────────
  // When projects are selected → narrow builder list to those projects' builders
  // When builders are selected → narrow project list to those builders' projects
  // Status filter cascades into both lists

  // Step 1: Status-filtered base set
  const statusFilteredProjects = useMemo(() => {
    if (filters.projectStatus === 'all') return projects;
    return projects.filter((p) => {
      const status = p.status || 'active';
      return filters.projectStatus === 'active'
        ? status === 'active'
        : status === 'completed';
    });
  }, [projects, filters.projectStatus]);

  // Step 2: Project options, narrowed by selected builders
  const projectOptions = useMemo(() => {
    let pool = statusFilteredProjects;

    // If builders are selected, narrow to projects from those builders
    if (filters.selectedBuilderNames.length > 0) {
      const builderSet = new Set(filters.selectedBuilderNames);
      pool = pool.filter((p) => p.builder_name && builderSet.has(p.builder_name));
    }

    const inScopeIds = new Set(pool.map((p) => p.id));
    const inScope = pool.map((p) => ({
      value: p.id,
      label: p.job_location,
    }));

    // Keep selected-but-out-of-scope items visible (dimmed) so user can unselect
    const outOfScope = filters.selectedProjectIds
      .filter((id) => !inScopeIds.has(id))
      .map((id) => {
        const proj = projects.find((p) => p.id === id);
        return {
          value: id,
          label: proj ? `${proj.job_location} ⌀` : id,
        };
      });

    return [...inScope, ...outOfScope];
  }, [statusFilteredProjects, filters.selectedBuilderNames, filters.selectedProjectIds, projects]);

  // Step 3: Builder options, narrowed by selected projects
  const builderOptions = useMemo(() => {
    let pool = statusFilteredProjects;

    // If projects are selected, narrow to builders from those projects
    if (filters.selectedProjectIds.length > 0) {
      const projSet = new Set(filters.selectedProjectIds);
      pool = pool.filter((p) => projSet.has(p.id));
    }

    const inScopeNames = new Set<string>();
    for (const p of pool) {
      if (p.builder_name) inScopeNames.add(p.builder_name);
    }

    const inScope = Array.from(inScopeNames)
      .sort()
      .map((b) => ({ value: b, label: b }));

    // Keep selected-but-out-of-scope builders visible
    const outOfScope = filters.selectedBuilderNames
      .filter((name) => !inScopeNames.has(name))
      .map((name) => ({ value: name, label: `${name} ⌀` }));

    return [...inScope, ...outOfScope];
  }, [statusFilteredProjects, filters.selectedProjectIds, filters.selectedBuilderNames]);

  // Animated close
  const handleClose = useCallback(() => {
    setClosing(true);
    setTimeout(() => onClose(), 250);
  }, [onClose]);

  // ESC to close
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') handleClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [handleClose]);

  // Click outside modal
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) handleClose();
    },
    [handleClose],
  );

  // Prevent body scroll while modal is open
  useEffect(() => {
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, []);

  // Count how many projects match current filters
  const matchCount = useMemo(() => {
    let filtered = projects;
    if (filters.selectedProjectIds.length > 0) {
      const idSet = new Set(filters.selectedProjectIds);
      filtered = filtered.filter((p) => idSet.has(p.id));
    }
    if (filters.selectedBuilderNames.length > 0) {
      const nameSet = new Set(filters.selectedBuilderNames);
      filtered = filtered.filter(
        (p) => p.builder_name && nameSet.has(p.builder_name),
      );
    }
    if (filters.projectStatus !== 'all') {
      filtered = filtered.filter((p) => {
        const status = p.status || 'active';
        return filters.projectStatus === 'active'
          ? status === 'active'
          : status === 'completed';
      });
    }
    return filtered.length;
  }, [projects, filters]);

  return createPortal(
    <div
      ref={overlayRef}
      className={`dfb-overlay ${closing ? 'closing' : ''}`}
      onClick={handleOverlayClick}
    >
      <div className="dfb-modal" role="dialog" aria-modal="true" data-tour-id="filter-modal">
        {/* Header */}
        <div className="dfb-modal-header">
          <div className="dfb-modal-icon">
            <SlidersHorizontal size={20} />
          </div>
          <div className="dfb-modal-title">
            <h2>Dashboard Filters</h2>
            <p>Refine your data across all charts and metrics</p>
          </div>
          <button
            className="dfb-modal-close"
            onClick={handleClose}
            aria-label="Close filters"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="dfb-modal-body">
          {/* Row 1: Projects + Builders */}
          <div className="dfb-row-2col">
            <div className="dfb-section">
              <div className="dfb-section-label">
                <FolderOpen size={13} className="dfb-section-label-icon" />
                Projects
              </div>
              <FilterList
                options={projectOptions}
                selected={filters.selectedProjectIds}
                onChange={(ids) =>
                  dispatch({ type: 'SET_PROJECTS', payload: ids })
                }
                searchPlaceholder="Search projects..."
              />
            </div>

            <div className="dfb-section">
              <div className="dfb-section-label">
                <Users size={13} className="dfb-section-label-icon" />
                Builder / Client
              </div>
              <FilterList
                options={builderOptions}
                selected={filters.selectedBuilderNames}
                onChange={(names) =>
                  dispatch({ type: 'SET_BUILDERS', payload: names })
                }
                searchPlaceholder="Search builders..."
              />
            </div>
          </div>

          {/* Row 2: Date Period */}
          <div className="dfb-section" data-tour-id="date-presets">
            <div className="dfb-section-label">
              <Calendar size={13} className="dfb-section-label-icon" />
              Period
            </div>
            <div className="dfb-presets-grid">
              {DATE_PRESETS.map((preset) => (
                <button
                  key={preset.value}
                  type="button"
                  className={`dfb-preset-chip ${filters.datePreset === preset.value ? 'active' : ''}`}
                  onClick={() =>
                    dispatch({ type: 'SET_PRESET', payload: preset.value })
                  }
                >
                  {preset.label}
                </button>
              ))}
            </div>
            {filters.datePreset === 'custom' && (
              <div className="dfb-date-range">
                <div className="dfb-date-pair">
                  <select
                    className="dfb-date-select"
                    value={filters.dateRange.startMonth}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_DATE_RANGE',
                        payload: {
                          ...filters.dateRange,
                          startMonth: Number(e.target.value),
                        },
                      })
                    }
                  >
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select
                    className="dfb-date-select"
                    value={filters.dateRange.startYear}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_DATE_RANGE',
                        payload: {
                          ...filters.dateRange,
                          startYear: Number(e.target.value),
                        },
                      })
                    }
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
                <span className="dfb-date-separator">to</span>
                <div className="dfb-date-pair">
                  <select
                    className="dfb-date-select"
                    value={filters.dateRange.endMonth}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_DATE_RANGE',
                        payload: {
                          ...filters.dateRange,
                          endMonth: Number(e.target.value),
                        },
                      })
                    }
                  >
                    {MONTHS.map((m, i) => (
                      <option key={m} value={i + 1}>
                        {m}
                      </option>
                    ))}
                  </select>
                  <select
                    className="dfb-date-select"
                    value={filters.dateRange.endYear}
                    onChange={(e) =>
                      dispatch({
                        type: 'SET_DATE_RANGE',
                        payload: {
                          ...filters.dateRange,
                          endYear: Number(e.target.value),
                        },
                      })
                    }
                  >
                    {yearOptions.map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>

          {/* Row 3: Status */}
          <div className="dfb-section">
            <div className="dfb-section-label">
              <Activity size={13} className="dfb-section-label-icon" />
              Project Status
            </div>
            <div className="dfb-status-control">
              {(['active', 'completed', 'all'] as const).map((status) => (
                <button
                  key={status}
                  type="button"
                  className={`dfb-status-btn ${filters.projectStatus === status ? 'active' : ''}`}
                  onClick={() =>
                    dispatch({ type: 'SET_STATUS', payload: status })
                  }
                >
                  {status === 'active'
                    ? 'Active'
                    : status === 'completed'
                      ? 'Completed'
                      : 'All'}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="dfb-modal-footer">
          <div className="dfb-footer-info">
            Showing <strong>{matchCount}</strong> of{' '}
            <strong>{projects.length}</strong> projects
          </div>
          <div className="dfb-footer-actions">
            {isFiltered && (
              <button
                className="dfb-btn-clear"
                onClick={() => dispatch({ type: 'CLEAR_ALL' })}
              >
                Clear All
              </button>
            )}
            <button className="dfb-btn-apply" onClick={handleClose}>
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ─── Main Export: Trigger + Active Pills + Modal ─────────────────────────────

export function DashboardFilterBar({ projects }: DashboardFilterBarProps) {
  const { filters, dispatch, activeFilterCount, isFiltered } = useDashboardFilter();
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <>
      {/* Trigger Row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 'var(--spacing-lg, 1.5rem)',
          flexWrap: 'wrap',
        }}
      >
        <button
          className={`dfb-trigger ${isFiltered ? 'has-filters' : ''}`}
          onClick={() => setIsModalOpen(true)}
          aria-label="Open dashboard filters"
          data-tour-id="filter-trigger"
        >
          <span className="dfb-trigger-icon">
            <SlidersHorizontal size={16} />
          </span>
          Filters
          {activeFilterCount > 0 && (
            <span className="dfb-trigger-count">{activeFilterCount}</span>
          )}
        </button>

        {/* Active filter pills (removable inline) */}
        {isFiltered && (
          <div className="dfb-active-pills">
            {filters.selectedProjectIds.length > 0 && (
              <span className="dfb-pill">
                {filters.selectedProjectIds.length} project
                {filters.selectedProjectIds.length > 1 ? 's' : ''}
                <button
                  className="dfb-pill-x"
                  onClick={() =>
                    dispatch({ type: 'SET_PROJECTS', payload: [] })
                  }
                  aria-label="Remove project filter"
                >
                  <X size={8} />
                </button>
              </span>
            )}
            {filters.selectedBuilderNames.length > 0 && (
              <span className="dfb-pill">
                {filters.selectedBuilderNames.length} builder
                {filters.selectedBuilderNames.length > 1 ? 's' : ''}
                <button
                  className="dfb-pill-x"
                  onClick={() =>
                    dispatch({ type: 'SET_BUILDERS', payload: [] })
                  }
                  aria-label="Remove builder filter"
                >
                  <X size={8} />
                </button>
              </span>
            )}
            {filters.datePreset !== 'all_time' && (
              <span className="dfb-pill">
                {DATE_PRESETS.find((p) => p.value === filters.datePreset)
                  ?.label || 'Custom'}
                <button
                  className="dfb-pill-x"
                  onClick={() =>
                    dispatch({ type: 'SET_PRESET', payload: 'all_time' })
                  }
                  aria-label="Remove date filter"
                >
                  <X size={8} />
                </button>
              </span>
            )}
            {filters.projectStatus !== 'active' && (
              <span className="dfb-pill">
                {filters.projectStatus === 'completed'
                  ? 'Completed'
                  : 'All Status'}
                <button
                  className="dfb-pill-x"
                  onClick={() =>
                    dispatch({ type: 'SET_STATUS', payload: 'active' })
                  }
                  aria-label="Remove status filter"
                >
                  <X size={8} />
                </button>
              </span>
            )}
          </div>
        )}
      </div>

      {/* Modal Portal */}
      {isModalOpen && (
        <FilterModal
          projects={projects}
          onClose={() => setIsModalOpen(false)}
        />
      )}
    </>
  );
}
